import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, InputLabel, MenuItem, Select, TextField,
    Typography, Box, IconButton
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { postUsuario } from '../../../services/apis';
import { AlertError } from '../../../utils/componentes-utiles/AlertError';
import { ContasenaOculta } from '../../../utils/componentes-utiles/ContasenaOculta';
import { generarColorAleatorio } from '../../../utils/utils';

// Hook para detectar clics fuera del componente
const useClickOutside = (ref, callback) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, callback]);
};

export const FormCrearUsuario = ({ 
    open, 
    setOpen, 
    cargarUsuarios, 
    roles, 
    departamentos 
}) => {
    // Estados
    const [errorAlert, setErrorAlert] = useState(false);
    const [errorPassAlert, setErrorPassAlert] = useState(false);
    const [errorRespuesta, setErrorRespuesta] = useState(false);
    const [mensajeError, setMensajeError] = useState("");
    const [contrasena, setContrasena] = useState("");
    const [confirmarContrasena, setConfirmarContrasena] = useState("");
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [cargando, setCargando] = useState(false);
    
    // Estado del usuario
    const [usuario, setUsuario] = useState({
        nombre: '',
        apellidos: '',
        mail: '',
        telefono: '',
        nif: '',
        id_rol: '',
        id_departamento: '',
        color: generarColorAleatorio(),
    });

    const [color, setColor] = useState(generarColorAleatorio());

    // Referencias
    const colorPickerRef = useRef(null);

    // Configurar el hook para cerrar al hacer clic fuera
    useClickOutside(colorPickerRef, () => {
        if (showColorPicker) {
            setShowColorPicker(false);
        }
    });

    // Filtrar roles y departamentos
    const rolesFiltrados = roles.filter(rol => rol.id !== 930 && rol.id !== 931);
    const departamentosFiltrados = departamentos.filter(depto => depto.id !== 30);

    // Sincronizar color con usuario
    useEffect(() => {
        setUsuario(prev => ({ ...prev, color: color }));
    }, [color]);

    // Resetear formulario cuando se abre/cierra
    useEffect(() => {
        if (!open) {
            resetFormulario();
        }
    }, [open]);

    const resetFormulario = () => {
        setUsuario({
            nombre: '',
            apellidos: '',
            mail: '',
            telefono: '',
            nif: '',
            id_rol: '',
            id_departamento: '',
            color: generarColorAleatorio(),
        });
        setColor(generarColorAleatorio());
        setContrasena("");
        setConfirmarContrasena("");
        setErrorAlert(false);
        setErrorPassAlert(false);
        setErrorRespuesta(false);
        setMensajeError("");
        setShowColorPicker(false);
        setCargando(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setUsuario(prev => ({ ...prev, [name]: value }));
        setErrorAlert(false);
    };

    const handleColorChange = (event) => {
        setColor(event.target.value);
    };

    const toggleColorPicker = () => {
        setShowColorPicker(!showColorPicker);
    };

    // Validaciones SIMPLIFICADAS
    const validarCampos = () => {
        // Campos obligatorios
        if (!usuario.nombre || !usuario.mail || !usuario.id_rol || !contrasena) {
            setMensajeError("Nombre, email, rol y contraseña son obligatorios");
            return false;
        }
        
        // Validación básica de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(usuario.mail)) {
            setMensajeError("Formato de email inválido");
            return false;
        }
        
        return true;
    };

    const validarContrasenas = () => {
        if (contrasena !== confirmarContrasena) {
            setMensajeError("Las contraseñas no coinciden");
            return false;
        }
        
        return true;
    };

    // NIF sin restricciones
    const handleNifChange = (event) => {
        let value = event.target.value.toUpperCase();
        setUsuario(prev => ({ ...prev, nif: value }));
        setErrorAlert(false);
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        console.log("== CREANDO USUARIO ==");
        console.log("Usuario:", usuario);
        console.log("Contraseña:", contrasena ? "***" : "vacía");
        
        setErrorAlert(false);
        setErrorPassAlert(false);
        setErrorRespuesta(false);
        setCargando(true);
        
        // Primero validar contraseñas
        if (!validarContrasenas()) {
            console.log("Error en contraseñas");
            setErrorPassAlert(true);
            setCargando(false);
            return;
        }
        
        // Luego validar campos obligatorios
        if (!validarCampos()) {
            console.log("Error en campos obligatorios");
            setErrorAlert(true);
            setCargando(false);
            return;
        }
        
        // Preparar datos para envío
        const usuarioEnvio = {
            ...usuario,
            contrasena: contrasena,
            color: color,
            telefono: usuario.telefono ? usuario.telefono.replace(/\s/g, '') : ''
        };
        
        console.log("Datos a enviar:", usuarioEnvio);
        
        try {
            const response = await postUsuario(usuarioEnvio);
            console.log("Respuesta del servidor:", response);
            
            if (response && response.success == true) {
                console.log("Usuario creado exitosamente");
                if (cargarUsuarios) {
                    cargarUsuarios();
                }
                handleClose();
            } else {
                const errorMsg = response?.error || response?.message || "Limite de usuarios alcanzado, elimina alguno o ponte en contacto con soporte para ampliar tu plan";
                console.error("Error del servidor:", errorMsg);
                setMensajeError(errorMsg);
                setErrorRespuesta(true);
            }
        } catch (error) {
            console.error("Error de red:", error);
            setMensajeError("Error de conexión con el servidor");
            setErrorRespuesta(true);
        } finally {
            setCargando(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="sm"
        >
            <Grid container direction="row" alignItems="center" justifyContent="space-between">
                <DialogTitle id="form-dialog-title">
                    Nuevo/a Usuario/a
                </DialogTitle>
                <div style={{ paddingRight: '24px' }}>
                    <CancelIcon 
                        onClick={handleClose} 
                        style={{ cursor: 'pointer' }} 
                    />
                </div>
            </Grid>
            
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        
                        {/* Icono de usuario con selector de color */}
                        <Grid item xs={12} sx={{ textAlign: 'center', position: 'relative' }}>
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <IconButton 
                                    onClick={toggleColorPicker}
                                    sx={{ 
                                        p: 0,
                                        position: 'relative',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'scale(1.05)'
                                        }
                                    }}
                                >
                                    <AccountCircleIcon 
                                        sx={{ 
                                            fontSize: 100, 
                                            color: color,
                                            transition: 'color 0.3s ease',
                                            cursor: 'pointer'
                                        }} 
                                    />
                                </IconButton>
                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                    Haz click para seleccionar un color
                                </Typography>
                                
                                {showColorPicker && (
                                    <Box 
                                        ref={colorPickerRef}
                                        sx={{ 
                                            position: 'absolute',
                                            top: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            mt: 2,
                                            p: 2,
                                            bgcolor: 'background.paper',
                                            borderRadius: 2,
                                            boxShadow: 3,
                                            zIndex: 1000,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                            Selecciona un color
                                        </Typography>
                                        
                                        <input
                                            type="color"
                                            value={color}
                                            onChange={handleColorChange}
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: '20%',
                                                border: '3px solid #e0e0e0',
                                                cursor: 'pointer',
                                                display: 'block',
                                                margin: '0 auto'
                                            }}
                                        />                
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Nombre y Apellidos */}
                        <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Nombre *"
                                    variant="outlined"
                                    fullWidth
                                    name="nombre"
                                    value={usuario.nombre}
                                    onChange={handleChange}
                                    error={errorAlert && !usuario.nombre}
                                    disabled={cargando}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Apellidos"
                                    variant="outlined"
                                    fullWidth
                                    name="apellidos"
                                    value={usuario.apellidos}
                                    onChange={handleChange}
                                    disabled={cargando}
                                />
                            </Grid>
                        </Grid>

                        {/* Email y Teléfono */}
                        <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Email *"
                                    variant="outlined"
                                    fullWidth
                                    type="email"
                                    name="mail"
                                    value={usuario.mail}
                                    onChange={handleChange}
                                    error={errorAlert && !usuario.mail}
                                    disabled={cargando}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Teléfono"
                                    variant="outlined"
                                    fullWidth
                                    name="telefono"
                                    value={usuario.telefono}
                                    onChange={handleChange}
                                    placeholder="Ej: 612345678"
                                    disabled={cargando}
                                />
                            </Grid>
                        </Grid>

                        {/* NIF */}
                        <Grid item xs={12}>
                            <TextField
                                label="NIF/NIE"
                                variant="outlined"
                                fullWidth
                                name="nif"
                                value={usuario.nif}
                                onChange={handleNifChange}
                                placeholder="Introduce el NIF/NIE"
                                inputProps={{
                                    style: { textTransform: 'uppercase' }
                                }}
                                disabled={cargando}
                            />
                        </Grid>

                        {/* Contraseñas */}
                        <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <ContasenaOculta
                                    contrasena={contrasena}
                                    setContrasena={setContrasena}
                                    label="Contraseña *"
                                    error={errorPassAlert}
                                    disabled={cargando}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ContasenaOculta
                                    contrasena={confirmarContrasena}
                                    setContrasena={setConfirmarContrasena}
                                    label="Confirmar Contraseña *"
                                    error={errorPassAlert}
                                    disabled={cargando}
                                />
                            </Grid>
                        </Grid>

                        {/* Rol y Departamento */}
                        <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl 
                                    fullWidth 
                                    variant="outlined"
                                    error={errorAlert && !usuario.id_rol}
                                >
                                    <InputLabel id="select-rol-label">Rol *</InputLabel>
                                    <Select
                                        value={usuario.id_rol}
                                        name="id_rol"
                                        onChange={handleChange}
                                        label="Rol *"
                                        disabled={cargando}
                                    >
                                        <MenuItem value="">
                                            <em>Selecciona un rol</em>
                                        </MenuItem>
                                        {rolesFiltrados.map((rol) => (
                                            <MenuItem key={rol.id} value={rol.id}>
                                                {rol.nombre}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="select-departamento-label">
                                        Departamento
                                    </InputLabel>
                                    <Select
                                        value={usuario.id_departamento}
                                        name="id_departamento"
                                        onChange={handleChange}
                                        label="Departamento"
                                        disabled={cargando}
                                    >
                                        <MenuItem value="">
                                            <em>Sin departamento</em>
                                        </MenuItem>
                                        {departamentosFiltrados.map((departamento) => (
                                            <MenuItem key={departamento.id} value={departamento.id}>
                                                {departamento.nombre}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={handleClose} 
                    color="primary" 
                    variant="outlined"
                    disabled={cargando}
                >
                    Cancelar
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    color="primary" 
                    variant="contained"
                    disabled={cargando || !usuario.nombre || !usuario.mail || !usuario.id_rol || !contrasena}
                >
                    {cargando ? 'Creando...' : 'Crear Usuario'}
                </Button>
            </DialogActions>

            {/* Alertas de Error */}
            {errorAlert && (
                <AlertError 
                    open={errorAlert} 
                    setOpen={setErrorAlert} 
                    titulo="Error" 
                    mensaje={mensajeError || "Falta rellenar algún campo obligatorio"} 
                />
            )}
            
            {errorPassAlert && (
                <AlertError 
                    open={errorPassAlert} 
                    setOpen={setErrorPassAlert} 
                    titulo="Error" 
                    mensaje={mensajeError || "Error en las contraseñas"} 
                />
            )}
            
            {errorRespuesta && (
                <AlertError 
                    open={errorRespuesta} 
                    setOpen={setErrorRespuesta} 
                    titulo="Error" 
                    mensaje={mensajeError || "Error al crear el usuario"} 
                />
            )}
        </Dialog>
    );
};